import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../../../../../environments/environment';
import { ApiMethodService } from '../../../../tools/services/api/method-api/apiMethod.service';
import { ActionBackOfficeResponse } from '../../../models/api.model';
import { IssueTypeTableQuery } from '../../../models/customer-service/customer-service-issue-type.model';

@Injectable({
  providedIn: 'root',
})
export class ApiCustomerServiceIssueTypeService {
  private readonly baseUrl = environment.apiPrivateUrl;
  private readonly cookieService = inject(CookieService);
  private readonly apiMethodService = inject(ApiMethodService);

  // ================= API Functions =================

  /**
   * Retrieves issue types matching search and pagination queries
   */
  getIssueTypeForTable(query: IssueTypeTableQuery): Observable<any> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number | boolean> = {
      page: query.page ?? 1,
      pageSize: query.pageSize ?? 20,
    };

    if (query.searchField) params['searchField'] = query.searchField;
    if (query.searchText) params['searchText'] = query.searchText;
    if (query.active !== undefined) params['active'] = query.active;

    return this.apiMethodService.request<any>(
      '/CustomerService/getIssueTypeForTable',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getIssueTypeForTable',
        token,
        headerOnly: false,
        params,
      }
    );
  }

  /**
   * Retrieves detailed issue type config by ID
   */
  getIssueTypeById(id: number): Observable<any> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<any>(
      '/CustomerService/getIssueTypeById',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getIssueTypeById',
        token,
        headerOnly: false,
        params: { id },
      }
    );
  }

  /**
   * Retrieves issue type options list for dropdown selectors
   */
  getIssueTypeDropdown(activeOnly = true, customerSelectableOnly?: boolean): Observable<any> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number | boolean> = {
      activeOnly,
    };
    if (customerSelectableOnly !== undefined) {
      params['customerSelectableOnly'] = customerSelectableOnly;
    }

    return this.apiMethodService.request<any>(
      '/CustomerService/getIssueTypeDropdown',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getIssueTypeDropdown',
        token,
        headerOnly: false,
        params,
      }
    );
  }

  /**
   * Submits request to create a new issue type
   */
  insertIssueType(body: any): Observable<ActionBackOfficeResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      '/CustomerService/insertIssueType',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'insertIssueType',
        token,
        headerOnly: false,
        body,
      }
    );
  }

  /**
   * Submits request to update an existing issue type configurations
   */
  updateIssueType(id: number, body: any): Observable<ActionBackOfficeResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      `/CustomerService/updateIssueType/${id}`,
      'PATCH',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'updateIssueType',
        token,
        headerOnly: false,
        body,
      }
    );
  }
}
